/**
 * DeclinedSummary · iter303ak-fix25 — Declined · T10 · Action 10
 * ──────────────────────────────────────────────────────────────
 * Read-only accordion of every DECLINED child appraisal (from
 * parent.child_decisions). Shows appraisal_no, quote_type/variant,
 * decision timestamp, and source (Follow-up etc). Operator visually
 * confirms before advancing to letter preview.
 *
 * If the current appraisal has NO child_decisions map (single-quote
 * legacy appraisal), the summary shows one row for the appraisal itself.
 */
import React, { useMemo } from 'react';
import { AlertCircle, XCircle } from 'lucide-react';

export default function DeclinedSummary({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const params = action.params || {};

  const declinedChildren = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.accepted_variants_pick?.decisions
      || capturedData?.await_quote_response?.decisions
      || capturedData?.appraisal?.child_decisions
      || capturedData?.appraisal_record?.child_decisions
      || {};
    const rows = Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'declined')
      .map(([child_id, d]) => ({ child_id, ...d }));
    // If there are no children (legacy single-quote appraisal), fall
    // back to a single row summarising the parent itself.
    if (rows.length === 0) {
      const appr = capturedData?.appraisal || capturedData?.appraisal_record || {};
      if (appr.appraisal_no) {
        rows.push({
          child_id: appr._id || '',
          appraisal_no: appr.appraisal_no,
          quote_type_ref: appr.quote_type_ref,
          variant_label: appr.variant_label,
          decision: 'declined',
          fallback: true,
        });
      }
    }
    return rows;
  }, [capturedData]);

  const parentAppraisalNo = capturedData?.appraisal?.appraisal_no
    || capturedData?.appraisal_record?.appraisal_no
    || capturedData?.appraisal_no;

  return (
    <div className="space-y-4" data-testid="wf-declined-summary">
      <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4">
        <div className="flex items-start gap-3">
          <XCircle className="w-5 h-5 mt-0.5 shrink-0 text-rose-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-rose-900">Declined Quotes — Review Before Notifying Client</div>
            <div className="text-sm mt-1 text-rose-800">
              {params.prompt || 'Review the list of quotes the client declined. This is read-only — you confirm here, then move on to preview the declined letter.'}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-rose-600 font-semibold">Declined</div>
            <div className="text-lg font-bold text-rose-900 leading-none">{declinedChildren.length}</div>
          </div>
        </div>
      </div>

      {declinedChildren.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>No declined quotes found.</b> This workflow was likely started by mistake — the appraisal has no declined child decisions recorded. Cancel this run and check the Follow-up outcome.
          </div>
        </div>
      )}

      {declinedChildren.length > 0 && (
        <div className="space-y-2">
          {declinedChildren.map((c) => {
            const label = [c.quote_type_ref, c.variant_label].filter(Boolean).join(' · ');
            const declinedAt = c.decided_at || c.decision_at || c.timestamp;
            const source = c.source || c.origin || 'Quote Sent Follow-up';
            return (
              <div key={c.child_id} className="p-3 border border-rose-200 bg-white rounded-lg" data-testid={`wf-declined-summary-row-${c.child_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">{label || 'Quote'}</span>
                    {c.appraisal_no && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-rose-800 bg-rose-100 border border-rose-300 rounded px-1.5 py-0.5">
                        A{c.appraisal_no}
                      </span>
                    )}
                    {c.fallback && (
                      <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                        Single-quote appraisal
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 text-right shrink-0">
                    {declinedAt && <div>Declined: {new Date(declinedAt).toLocaleString()}</div>}
                    <div className="italic">Source: {source}</div>
                  </div>
                </div>
                {c.note && (
                  <div className="mt-2 text-[11px] text-slate-600 border-t border-slate-100 pt-2 italic">
                    Note: {c.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {parentAppraisalNo && (
        <div className="text-[11px] text-slate-500 italic">
          Parent appraisal: <b>A{parentAppraisalNo}</b>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-declined-summary-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-declined-summary-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={declinedChildren.length === 0}
            className="px-4 py-1.5 rounded-md text-sm bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40"
            data-testid="wf-declined-summary-continue"
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}
